import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { getStoredUtm, trackFunnelEvent } from '@/utils/tracking';

const API = '/api';

const INITIAL_STEPS = {
  step1: { country: '', region: '', postcode: '', home_type: '', ownership: '' },
  step2: { build_period: '', size: '', energy_label: '' },
  step3: { glazing_type: '', roof_insulation: '', wall_insulation: '', floor_insulation: '', drafts: '' },
  step4: { heating_type: '', floor_heating: false, ventilation_type: '', solar_status: '', electricity_usage: '', electric_vehicle: '', ev_home_charging: '', ev_terrain: '' },
  step5: { main_goal: '', energy_bill_feeling: '', investment_budget: '', execution_preference: '', email: '' },
};

const VALIDATION_RULES = {
  1: { required: ['country', 'home_type', 'ownership'] },
  2: { required: ['build_period', 'size'] },
  3: { required: ['glazing_type', 'roof_insulation', 'wall_insulation', 'floor_insulation'] },
  4: { required: ['heating_type', 'ventilation_type'] },
  5: { required: ['main_goal', 'investment_budget', 'email'] },
};

export default function useQuizState() {
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [steps, setSteps] = useState(INITIAL_STEPS);

  useEffect(() => {
    trackFunnelEvent('scan_started', { country: steps.step1.country });
  }, []);
  const [errors, setErrors] = useState({});
  const [apartmentDetails, setApartmentDetails] = useState({ floor_level: '', shared_building: '', shell_influence: '' });
  const [monumentDetails, setMonumentDetails] = useState({ protected_status: '', facade_restrictions: false, roof_restrictions: false, window_restrictions: false });
  const [detachedDetails, setDetachedDetails] = useState({ technical_space: '', large_heat_demand: false });

  const totalSteps = 5;
  const progress = (currentStep / totalSteps) * 100;

  const updateStep = (stepKey, updates) => {
    setSteps(prev => ({ ...prev, [stepKey]: { ...prev[stepKey], ...updates } }));
    setErrors({});
  };

  const handleCountryChange = (country) => {
    updateStep('step1', { country });
    const langMap = { NL: 'nl', BE: 'nl', DE: 'de', FR: 'fr' };
    const suggestedLang = langMap[country];
    if (suggestedLang && i18n.language !== suggestedLang) {
      i18n.changeLanguage(suggestedLang);
    }
  };

  const validateStep = () => {
    const rules = VALIDATION_RULES[currentStep];
    if (!rules) return true;

    const stepKey = `step${currentStep}`;
    const data = steps[stepKey];
    const newErrors = {};

    for (const field of rules.required) {
      if (!data[field] || data[field].toString().trim() === '') {
        newErrors[field] = t('validation.required');
      }
    }

    if (currentStep === 5 && data.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(data.email)) {
        newErrors.email = t('validation.invalidEmail');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (!validateStep()) {
      toast.error(t('validation.fixErrors'));
      return;
    }
    trackFunnelEvent('step_completed', { step: currentStep, country: steps.step1.country });
    if (currentStep < totalSteps) setCurrentStep(currentStep + 1);
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
    setErrors({});
  };

  const isApartment = steps.step1.home_type?.toLowerCase().includes('apartment');
  const isMonument = steps.step1.home_type?.toLowerCase().includes('monument') || steps.step1.home_type?.toLowerCase().includes('protected');
  const isDetached = steps.step1.home_type?.toLowerCase().includes('detached') && !steps.step1.home_type?.toLowerCase().includes('semi');

  const handleSubmit = async () => {
    if (!validateStep()) {
      toast.error(t('validation.fixErrors'));
      return;
    }
    setLoading(true);
    try {
      const utm = getStoredUtm();
      const payload = {
        ...steps,
        language: i18n.language,
        utm_source: utm.utm_source || null,
        utm_medium: utm.utm_medium || null,
        utm_campaign: utm.utm_campaign || null,
        utm_content: utm.utm_content || null,
        utm_term: utm.utm_term || null,
      };
      if (isApartment) payload.apartment_details = apartmentDetails;
      if (isMonument) payload.monument_details = monumentDetails;
      if (isDetached) payload.detached_details = detachedDetails;

      let response;
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          response = await axios.post(`${API}/quiz/submit`, payload);
          break;
        } catch (err) {
          if (attempt === 0) {
            await new Promise(r => setTimeout(r, 1500));
          } else {
            throw err;
          }
        }
      }
      if (response?.data?.success) {
        trackFunnelEvent('scan_completed', { report_id: response.data.report_id, country: steps.step1.country });
        if (window.pwTrack) window.pwTrack('scan_complete', { report_id: response.data.report_id });
        toast.success(t('quiz.reportGenerated'));
        navigate(`/report/${response.data.report_id}`);
      }
    } catch {
      toast.error(t('quiz.submitError'));
    } finally {
      setLoading(false);
    }
  };

  return {
    currentStep, totalSteps, progress, loading,
    steps, errors, updateStep, handleCountryChange,
    handleNext, handleBack, handleSubmit, validateStep,
    isApartment, isMonument, isDetached,
    apartmentDetails, setApartmentDetails,
    monumentDetails, setMonumentDetails,
    detachedDetails, setDetachedDetails,
  };
}
